import { useState } from 'react';
import {
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconSchool } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import {
  ACCESS_RULE_LABELS,
  TRAINING_BLOCK_LABELS,
  createTrainingMaterialSchema,
  type CreateTrainingMaterialInput,
} from '@creativitas/shared';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateViews';
import { toUserMessage } from '@/lib/api';
import { ADMIN_TRAINING_KEY, createMaterial, fetchAdminTraining } from './content-api';

/** Training catalogue for the admin (§45, §48). */
export function AdminTrainingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const query = useQuery({ queryKey: ADMIN_TRAINING_KEY, queryFn: fetchAdminTraining });

  const form = useForm<CreateTrainingMaterialInput>({
    initialValues: { title: '', description: null },
    validate: zodResolver(createTrainingMaterialSchema),
  });

  const createMutation = useMutation({
    mutationFn: createMaterial,
    onSuccess: (material) => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_TRAINING_KEY });
      setCreating(false);
      form.reset();
      void navigate(`/admin/training/${material.id}`);
    },
    onError: (error) =>
      notifications.show({
        title: 'Не удалось создать материал',
        message: toUserMessage(error),
        color: 'red',
      }),
  });

  const items = query.data?.items ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Администрирование"
        title="Обучение"
        description="Простые публикуемые материалы: текст, видео, аудио, ссылки и PDF. Это не LMS — без тестов и домашних заданий."
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
            Новый материал
          </Button>
        }
      />

      {query.isLoading ? (
        <LoadingState label="Загружаем материалы..." />
      ) : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<IconSchool size={28} stroke={1.5} />}
          title="Материалов пока нет"
          action={<Button onClick={() => setCreating(true)}>Новый материал</Button>}
        />
      ) : (
        <Paper withBorder radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
          <ScrollArea>
            <Table verticalSpacing="sm" horizontalSpacing="md" miw={800} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Материал</Table.Th>
                  <Table.Th>Блоки</Table.Th>
                  <Table.Th>Статус</Table.Th>
                  <Table.Th>Доступ</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((material) => (
                  <Table.Tr key={material.id}>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text fw={600} size="sm">
                          {material.title}
                        </Text>
                        <Text size="xs" c="var(--text-subtle)">
                          {material.slug}
                        </Text>
                      </Stack>
                    </Table.Td>

                    <Table.Td>
                      <Group gap={4}>
                        <Text size="sm">{material.blockCount}</Text>
                        {material.blockTypes.map((type) => (
                          <Badge key={type} size="xs" variant="default">
                            {TRAINING_BLOCK_LABELS[type]}
                          </Badge>
                        ))}
                      </Group>
                    </Table.Td>

                    <Table.Td>
                      <Badge
                        size="sm"
                        variant={material.status === 'published' ? 'light' : 'default'}
                        color={material.status === 'published' ? 'teal' : 'gray'}
                      >
                        {material.status === 'published' ? 'Опубликован' : 'Черновик'}
                      </Badge>
                    </Table.Td>

                    <Table.Td>
                      <Stack gap={4} align="flex-start">
                        {material.accessRules.length === 0 ? (
                          <Badge size="xs" variant="light" color="gold">
                            не настроен
                          </Badge>
                        ) : (
                          <Group gap={4}>
                            {material.accessRules.map((rule) => (
                              <Badge key={rule} size="xs" variant="default">
                                {ACCESS_RULE_LABELS[rule]}
                              </Badge>
                            ))}
                          </Group>
                        )}
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          onClick={() => void navigate(`/admin/training/${material.id}?tab=access`)}
                          data-testid="open-access"
                        >
                          Настроить доступ
                        </Button>
                      </Stack>
                    </Table.Td>

                    <Table.Td>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => void navigate(`/admin/training/${material.id}`)}
                      >
                        Открыть
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      )}

      <Modal opened={creating} onClose={() => setCreating(false)} title="Новый материал">
        <form onSubmit={form.onSubmit((values) => createMutation.mutate(values))}>
          <Stack gap="md">
            <TextInput label="Название" required {...form.getInputProps('title')} />
            <Textarea
              label="Описание"
              minRows={2}
              value={form.values.description ?? ''}
              onChange={(event) => form.setFieldValue('description', event.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCreating(false)}>
                Отмена
              </Button>
              <Button type="submit" loading={createMutation.isPending}>
                Создать
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
